<template>
  <div class="home-container">
    <!-- 轮播图区域 -->
    <div class="banner-section">
      <el-carousel height="400px" :interval="5000" arrow="always" indicator-position="outside">
        <el-carousel-item v-for="(item, index) in bannerList" :key="index">
          <div class="banner-item" :style="{ backgroundImage: `url(${item.image})` }">
            <div class="banner-content">
              <h2 class="banner-title">{{ item.title }}</h2>
              <p class="banner-desc">{{ item.description }}</p>
              <el-button type="primary" size="large" @click="navigateTo(item.link)" v-if="item.link">
                {{ item.buttonText }}
              </el-button>
            </div>
          </div>
        </el-carousel-item>
      </el-carousel>
    </div>
    
    <!-- 服务卡片区域 -->
    <div class="section service-section">
      <div class="section-header">
        <h2 class="section-title">园区服务</h2>
        <p class="section-desc">一站式智慧园区解决方案，满足您的各种需求</p>
      </div>
      
      <div class="service-grid">
        <div class="service-card" v-for="(service, index) in serviceList" :key="index" @click="navigateTo(service.link)">
          <div class="service-icon">
            <img v-if="service.image" :src="service.image" alt="service.title" width="60" height="60" />
            <el-icon v-else :size="40">
            <component :is="service.icon"></component>
          </el-icon>
          </div>
          <h3 class="service-title">{{ service.title }}</h3>
          <p class="service-desc">{{ service.description }}</p>
        </div>
      </div>
    </div>
    
    <!-- 活动列表、培训和通知区域 -->
    <div class="section announcement-section">
      <div class="announcement-grid">
        <!-- 活动列表 -->
        <div class="announcement-card">
          <div class="card-header">
            <h3 class="card-title">活动列表</h3>
            <router-link to="/service/activity" class="view-more">查看更多</router-link>
          </div>
          
          <div v-loading="activityLoading" class="news-list">
            <div class="news-item" v-for="(activity, index) in activityList" :key="activity.id || index" @click="navigateTo(activity.website || `/service/activity/detail/${activity.id}`)">
              <div class="news-content">
                <h4 class="news-title" :title="activity.title">{{ activity.title }}</h4>
                <p class="news-time">{{ activity.remark }}</p>
              </div>
              <el-icon><ArrowRight /></el-icon>
            </div>
            
            <el-empty v-if="!activityLoading && activityList.length === 0" description="暂无活动" :image-size="60" />
          </div>
        </div>
        
        <!-- 培训列表 -->
        <div class="announcement-card">
          <div class="card-header">
            <h3 class="card-title">培训列表</h3>
            <router-link to="/service/training" class="view-more">查看更多</router-link>
          </div>
          
          <div v-loading="trainingLoading" class="news-list">
            <div class="news-item" v-for="(training, index) in trainingList" :key="training.id || index" @click="navigateTo(training.website || `/service/training/detail/${training.id}`)">
              <div class="news-content">
                <h4 class="news-title" :title="training.title">{{ training.title }}</h4>
                <p class="news-time">{{ training.remark }}</p>
              </div>
              <el-icon><ArrowRight /></el-icon>
            </div>
            
            <el-empty v-if="!trainingLoading && trainingList.length === 0" description="暂无培训" :image-size="60" />
          </div>
        </div>
        
        <!-- 通知公告 -->
        <div class="announcement-card">
          <div class="card-header">
            <h3 class="card-title">通知公告</h3>
            <router-link to="/service/announcement" class="view-more">查看更多</router-link>
          </div>
          
          <div v-loading="noticeLoading" class="news-list">
            <div class="news-item" v-for="(notice, index) in noticeList" :key="notice.id || index" @click="navigateTo(notice.link)">
              <div class="news-content">
                <h4 class="news-title" :title="notice.title">{{ notice.title }}</h4>
                <p class="news-time">{{ notice.time }}</p>
              </div>
              <el-icon><ArrowRight /></el-icon>
            </div>
            
            <el-empty v-if="!noticeLoading && noticeList.length === 0" description="暂无通知公告" :image-size="60" />
          </div>
        </div>

        <!-- 人才服务（已隐藏）
        <div class="announcement-card">
          <div class="card-header">
            <h3 class="card-title">人才服务</h3>
          </div>

          <div v-loading="talentLoading" class="news-list">
            <div class="news-item" v-for="(item, index) in talentServiceList" :key="index" @click="navigateTo(item.website)">
              <div class="news-content">
                <h4 class="news-title" :title="item.caidan">{{ item.caidan }}</h4>
              </div>
              <el-icon><ArrowRight /></el-icon>
            </div>

            <el-empty v-if="!talentLoading && talentServiceList.length === 0" description="暂无人才服务" :image-size="60" />
          </div>
        </div>
        -->
      </div>
    </div>
    
    <!-- 入驻展示区域 -->
    <div class="section apply-section">
      <div class="apply-content">
        <h2 class="apply-title">申请入驻智慧园区</h2>
        <p class="apply-desc">享受一站式企业服务，加入创新创业生态圈</p>
        <el-button type="primary" size="large" @click="navigateTo('/park/application')">立即申请</el-button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { 
  HomeFilled, 
  OfficeBuilding, 
  Document, 
  Service, 
  Money, 
  Connection,
  User,
  ArrowRight,
  Calendar
} from '@element-plus/icons-vue'
import { serviceRequest, config } from '@/qingyu'

const router = useRouter()
const noticeLoading = ref(false)
const activityLoading = ref(false)
const trainingLoading = ref(false)
const talentLoading = ref(false)

// 轮播图数据
const bannerList = ref([
  {
    title: '智慧园区综合服务平台',
    description: '提供一站式园区管理和企业服务解决方案',
    image: require('@/assets/images/banners/smart-park.svg'),
    link: '/service',
    buttonText: '了解更多'
  },
  {
    title: '园区入驻申请',
    description: '快速便捷的线上申请流程，专业团队为您服务',
    image: require('@/assets/images/banners/park-application.svg'),
    link: '/park/application',
    buttonText: '立即申请'
  },
  {
    title: '企业服务中心',
    description: '专业的企业服务，助力企业发展',
    image: require('@/assets/images/banners/enterprise-service.svg'),
    link: '/service',
    buttonText: '查看服务'
  }
])

// 服务卡片数据
const serviceList = ref([
  {
    title: '园区服务',
    description: '查看园区分布、设施、企业等信息',
    icon: 'OfficeBuilding',
    image: require('@/assets/images/icons/park-service.svg'),
    link: '/park/list'
  },
  {
    title: '入驻申请',
    description: '在线提交企业入驻申请',
    icon: 'Document',
    image: require('@/assets/images/icons/application.svg'),
    link: '/park/application'
  },
  {
    title: '合同管理',
    description: '查看和管理租赁合同',
    icon: 'Document',
    image: require('@/assets/images/icons/contract.svg'),
    link: '/park/list'
  },
  {
    title: '会议预约',
    description: '会议室预约及使用管理',
    icon: 'Calendar',
    image: require('@/assets/images/icons/meeting.svg'),
    link: '/service/property/facility'
  },
  {
    title: '物业服务',
    description: '物业报修、会议室预约等服务',
    icon: 'Service',
    image: require('@/assets/images/icons/property.svg'),
    link: '/service/property'
  },
  {
    title: '企业服务',
    description: '科技、金融、人才等企业服务',
    icon: 'Connection',
    image: require('@/assets/images/icons/enterprise.svg'),
    link: '/report/list'
  },
  {
    title: '专利服务',
    description: '查看和管理专利信息',
    icon: 'Document',
    image: require('@/assets/images/icons/zhuanli.svg'), // 移除 SVG 文件引用
    link: '/service/zhuanli/list'
  },
  // {
  //   title: '软著服务',
  //   description: '查看和管理软件著作权信息',
  //   icon: 'Document',
  //   image: require('@/assets/images/icons/ruanzhu.svg'),
  //   link: '/service/ruanzhu/list'
  // }
])

// 活动列表数据
const activityList = ref([])

// 培训列表数据
const trainingList = ref([])

// 通知公告数据
const noticeList = ref([])

// 人才服务数据
const talentServiceList = ref([])

// 获取活动列表
const getActivityList = async () => {
  activityLoading.value = true
  try {
    const params = {
      page: 1,
      pageSize: 4 // 首页只显示4条
    }
    
    const response = await serviceRequest.post(config.api.ENTERPRISE.ACTIVITY_LIST, params)
    if (response && response.data) {
      activityList.value = response.data.map(item => ({
        id: item.id,
        title: item.title,
        remark: item.remark,
        website: item.website
      }))
    }
  } finally {
    activityLoading.value = false
  }
}

// 获取培训列表
const getTrainingList = async () => {
  trainingLoading.value = true
  try {
    const params = {
      page: 1,
      pageSize: 4 // 首页只显示4条
    }
    
    const response = await serviceRequest.post(config.api.ENTERPRISE.TRAINING_LIST, params)
    if (response && response.data) {
      trainingList.value = response.data.map(item => ({
        id: item.id,
        title: item.title,
        remark: item.remark,
        website: item.website
      }))
    }
  } finally {
    trainingLoading.value = false
  }
}

// 获取通知公告列表
const getNoticeList = async () => {
  noticeLoading.value = true
  try {
    const params = {
      page: 1,
      pageSize: 4 // 首页只显示4条
    }
    
    const response = await serviceRequest.post(config.api.ENTERPRISE.NOTICE_LIST, params)
    if (response && response.data) {
      noticeList.value = response.data.map(item => ({
        id: item.id,
        title: item.title,
        remark: item.remark,
        link: item.website || `/service/announcement/detail/${item.id}`,
        time: new Date().toLocaleDateString() // API中未返回时间，使用当前日期
      }))
    }
  } finally {
    noticeLoading.value = false
  }
}

// 获取人才服务配置
const getTalentServices = async () => {
  talentLoading.value = true
  try {
    const response = await serviceRequest.getTalentServiceConfig()
    if (response) {
      talentServiceList.value = response
    }
  } finally {
    talentLoading.value = false
  }
}

// 页面导航
const navigateTo = (path) => {
  if (path) {
    // 检查是否是外部链接
    if (path.startsWith('http')) {
      window.open(path, '_blank')
    } else {
      router.push(path)
    }
  }
}

// 生命周期钩子
onMounted(() => {
  // 获取活动列表
  getActivityList()
  // 获取培训列表
  getTrainingList()
  // 获取通知公告列表
  getNoticeList()
  // 获取人才服务配置（已隐藏）
  // getTalentServices()
})
</script>

<style lang="scss" scoped>
.home-container {
  width: 100%;
}

.section {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  
  .section-header {
    text-align: center;
    margin-bottom: 40px;
    
    .section-title {
      font-size: 28px;
      font-weight: 600;
      color: #333;
      margin: 0 0 10px;
    }
    
    .section-desc {
      font-size: 16px;
      color: #666;
      margin: 0;
    }
  }
}

/* 轮播图样式 */
.banner-section {
  margin-bottom: 20px;
  
  .banner-item {
    height: 100%;
    background-size: cover;
    background-position: center;
    display: flex;
    align-items: center;
    
    .banner-content {
      width: 100%;
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 20px;
      color: #fff;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
      
      .banner-title {
        font-size: 36px;
        font-weight: 600;
        margin: 0 0 10px;
      }
      
      .banner-desc {
        font-size: 18px;
        margin: 0 0 20px;
        max-width: 600px;
      }
    }
  }
}

/* 服务卡片样式 */
.service-section {
  background-color: #fff;
  
  .service-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
  }
  
  .service-card {
    background-color: #f7f8fa;
    border-radius: 8px;
    padding: 30px 20px;
    text-align: center;
    transition: all 0.3s ease;
    cursor: pointer;
    
    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
      background-color: #fff;
    }
    
    .service-icon {
      color: var(--el-color-primary);
      margin-bottom: 15px;
    }
    
    .service-title {
      font-size: 18px;
      font-weight: 600;
      margin: 0 0 10px;
      color: #333;
    }
    
    .service-desc {
      font-size: 14px;
      color: #666;
      margin: 0;
      line-height: 1.5;
    }
  }
}

/* 园区动态和通知区域样式 */
.announcement-section {
  background-color: #f7f8fa;
  padding-left: 0;
  padding-right: 0;
  .announcement-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
  }
  
  .announcement-card {
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
    padding: 20px;
    
    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 15px;
      
      .card-title {
        font-size: 18px;
        font-weight: 600;
        color: #333;
        margin: 0;
      }
      
      .view-more {
        font-size: 14px;
        color: var(--el-color-primary);
        text-decoration: none;
      }
    }
    
    .news-list {
      .news-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 0;
        border-bottom: 1px solid #f0f0f0;
        cursor: pointer;
        
        &:last-child {
          border-bottom: none;
        }
        
        &:hover {
          .news-title {
            color: var(--el-color-primary);
          }
        }
        
        .news-content {
          flex: 1;
          overflow: hidden;
          
          .news-title {
            font-size: 15px;
            font-weight: 500;
            color: #333;
            margin: 0 0 5px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          
          .news-time {
            font-size: 12px;
            color: #999;
            margin: 0;
          }
        }
        
        .el-icon {
          color: #999;
          margin-left: 10px;
        }
      }
    }
  }
}

/* 入驻申请区域样式 */
.apply-section {
  background: linear-gradient(to right, #3a7bd5, #3a6073);
  color: #fff;
  text-align: center;
  padding: 60px 20px;
  margin-bottom: 0;
  
  .apply-title {
    font-size: 32px;
    font-weight: 600;
    margin: 0 0 15px;
  }
  
  .apply-desc {
    font-size: 18px;
    margin: 0 0 30px;
    opacity: 0.9;
  }
}

/* 响应式布局 */
@media screen and (max-width: 992px) {
  .service-section .service-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media screen and (max-width: 992px) {
  .announcement-section .announcement-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media screen and (max-width: 768px) {
  .banner-section .banner-item .banner-content {
    .banner-title {
      font-size: 28px;
    }
    
    .banner-desc {
      font-size: 16px;
    }
  }
  
  .announcement-section .announcement-grid {
    grid-template-columns: 1fr;
  }
  
  .apply-section {
    padding: 40px 20px;
    
    .apply-title {
      font-size: 24px;
    }
    
    .apply-desc {
      font-size: 16px;
    }
  }
}

@media screen and (max-width: 576px) {
  .service-section .service-grid {
    grid-template-columns: 1fr;
  }
  
  .section {
    padding: 30px 15px;
  }
}
</style>
